import React, { useEffect, useMemo, useState } from 'react';
import { ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useReefConfig } from '../../../../hooks/UseReefConfig';
import { ListDocumentRequest } from '../../../../services/soap/document_library/requests/ListDocumentRequest';
import { invokeNewMessageCreating } from '../../../../utils/NavigationUtils';
import DocumentPreviewComponent, { PreviewDocument } from '../../../../components/protected/layout-components/DocumentPreview/DocumentPreviewComponent';

interface IIssueDocumentsViewProps {
  issue: any;
  refreshKey?: number;
  onUploadDocument?: () => void;
}

const normalizeIssueDocuments = (documentValue: any): PreviewDocument[] => {
  if (!documentValue) return [];
  const documents = Array.isArray(documentValue) ? documentValue : [documentValue];
  return documents
    .map((item: any) => ({
      documentId: String(item?.documentId || item?.id || ''),
      name: String(item?.name || item?.fileName || ''),
      fileName: String(item?.fileName || item?.name || ''),
      mimeContentType: String(item?.mimeContentType || ''),
    }))
    .filter((item) => item.documentId);
};

const mergeDocuments = (...collections: PreviewDocument[][]): PreviewDocument[] => {
  const map = new Map<string, PreviewDocument>();
  collections.flat().forEach((document) => {
    if (!document?.documentId) return;
    map.set(String(document.documentId), document);
  });
  return Array.from(map.values());
};

const IssueDocumentsView: React.FC<IIssueDocumentsViewProps> = ({ issue, refreshKey = 0, onUploadDocument }) => {
  const { baseUrl } = useReefConfig();
  const { documentLibraryService, store } = useDependency();
  const [showLoader, setShowLoader] = useState(true);
  const [documents, setDocuments] = useState<PreviewDocument[]>([]);

  const fallbackDocuments = useMemo(() => normalizeIssueDocuments(issue?.DOCUMENT), [issue?.DOCUMENT]);

  useEffect(() => {
    let isActive = true;

    const loadDocuments = async () => {
      if (!issue?.ident) {
        if (isActive) {
          setDocuments(fallbackDocuments);
          setShowLoader(false);
        }
        return;
      }

      setShowLoader(true);
      try {
        const response = await documentLibraryService.listDocument(
          new ListDocumentRequest(store.Server, store.SessionId, {
            limit: 100,
            offset: 0,
            parent: String(issue.ident),
            orderBy: 'doc_file_name',
          })
        );

        const listedDocuments = Array.isArray(response?.DOCUMENT)
          ? response.DOCUMENT
          : response?.DOCUMENT
            ? [response.DOCUMENT]
            : [];

        const normalized = listedDocuments.map((item: any) => ({
          documentId: String(item?.id || item?.documentId || ''),
          name: String(item?.name || item?.fileName || ''),
          fileName: String(item?.fileName || item?.name || ''),
          mimeContentType: String(item?.mimeContentType || ''),
        })).filter((item: PreviewDocument) => item.documentId);

        const merged = mergeDocuments(fallbackDocuments, normalized);

        if (isActive) {
          setDocuments(merged);
        }
      } catch {
        if (isActive) {
          setDocuments(mergeDocuments(fallbackDocuments));
        }
      } finally {
        if (isActive) {
          setShowLoader(false);
        }
      }
    };

    loadDocuments();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, fallbackDocuments, issue?.ident, refreshKey, store.Server, store.SessionId]);

  const handleSendDocument = async () => {
    try {
      const attachments = await Promise.all(
        documents.map(async (document) => {
          const response = await fetch(
            `${baseUrl}/asp-cgi/doc-download.cgi?instance=${store.Server}&session=${store.SessionId}&documentId=${document.documentId}&disposition=inline`
          );
          const blob = await response.blob();
          const base64Files = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          return {
            fileName: document.fileName || document.name,
            fileSize: blob.size,
            base64Files,
          };
        })
      );

      invokeNewMessageCreating(
        String(issue?.ident || ''),
        'issue',
        undefined,
        undefined,
        true,
        attachments
      );
    } catch {
      ToastQueue.negative('Unable to prepare documents for sending.', { timeout: 2000 });
    }
  };

  if (showLoader) {
    return (
      <View UNSAFE_style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <ProgressCircle aria-label="Loading documents" isIndeterminate />
      </View>
    );
  }

  return (
    <DocumentPreviewComponent
      documents={documents}
      baseUrl={baseUrl}
      server={store.Server}
      session={store.SessionId}
      onSendDocument={handleSendDocument}
      onUploadDocument={onUploadDocument}
    />
  );
};

export default IssueDocumentsView;
